import React, {FunctionComponent, useCallback, useMemo} from "react";
import {
    QueryBuilderColumn,
    QueryBuilderReducerActionTypeEnum,
    QueryBuilderReducerDispatch,
    QueryBuilderStateQueryRule,
    ThemeMixin
} from "../../../index";
import {
    Checkbox,
    CheckboxProps,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    TextFieldProps,
    Theme,
    Tooltip,
    Unstable_Grid2 as Grid
} from "@mui/material";
import {DeleteOutlined} from "@mui/icons-material";
import QueryBuilderValueInput from "../value-input";

export interface QueryBuilderRuleProps {
    columns: QueryBuilderColumn[];
    rule: QueryBuilderStateQueryRule;
    ruleIndex: number;
    conditionIndex: number;
    dispatch: QueryBuilderReducerDispatch;
    nestedLevel?: number;
    disabled: boolean;
    last: boolean;
}

const QueryBuilderRule: FunctionComponent<QueryBuilderRuleProps> = ({
                                                                        columns,
                                                                        rule,
                                                                        ruleIndex,
                                                                        conditionIndex,
                                                                        dispatch,
                                                                        nestedLevel = 1,
                                                                        disabled,
                                                                        last,
                                                                    }) => {
    const column = useMemo(() => columns.find(column => column.field === rule.field), [rule.field, columns])

    const fieldOptions = useMemo(() => columns.map((column) => ({
        value: column.field,
        label: column.column,
    })), [columns])

    const operatorOptions = useMemo(() => {
        if (!rule.field)
            return [];
        return column?.operators?.map(operator => ({
                value: operator,
                label: operator,
            }))
            ?? [];
    }, [column, rule.field]);

    const nullable = useMemo(() => {
        if (!rule.field)
            return false;
        return column?.isNullable ?? false;
    }, [column, rule.field]);


    const showOnlyField = useMemo(() => !rule.field || !column?.operators?.length, [rule.field, column?.operators?.length]);

    const showOnlyFieldAndOperator = useMemo(() => !!rule.field && (!rule.operator && !!column?.operators?.length), [column?.operators?.length, rule.field, rule.operator]);

    /**
     * Changes the rule's field in the reducer.
     */
    const changeField = useCallback<Required<TextFieldProps>['onChange']>((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeField,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                field: e.target.value,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex]);


    /**
     * Changes the rule's operator in the reducer.
     */
    const changeOperator = useCallback<Required<TextFieldProps>['onChange']>((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeOperator,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                operator: e.target.value,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex]);

    /**
     * Removes a rule from the condition.
     */
    const deleteRule = useCallback(() => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.DeleteRule,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
            },
        })
    }, [dispatch, ruleIndex, conditionIndex]);

    /**
     * Changes the rule's [displayEmptyValuesOnly] in the reducer.
     */
    const changeDisplayEmptyValuesOnly = useCallback<Required<CheckboxProps>['onChange']>((e) => {
        dispatch({
            type: QueryBuilderReducerActionTypeEnum.ChangeDisplayEmptyValuesOnly,
            payload: {
                conditionIndex: conditionIndex,
                ruleIndex: ruleIndex,
                displayEmptyValuesOnly: e.target.checked,
            },
        })
    }, [dispatch, conditionIndex, ruleIndex])

    /**
     * The sx style mixin for the condition's box.
     */
    const paperMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            marginInlineStart: nestedLevel * 4,
            position: 'relative',
            backgroundColor: theme.palette.background.paper,
            padding: 2,
            '&:before': {
                position: 'absolute',
                content: '""',
                top: -16,
                left: -20,
                width: 20,
                height: `calc(50% + 16px)`,
                borderLeft: `1px dashed ${theme.palette.primary.main}`,
                borderBottom: `1px dashed ${theme.palette.primary.main}`,
            },
            ...(!last && {
                '&:after': {
                    position: 'absolute',
                    content: '""',
                    top: `calc(50% + 1px)`,
                    left: -20,
                    height: `calc(50% - 1px)`,
                    borderLeft: `1px dashed ${theme.palette.primary.main}`,
                },
            })
        };
    }, [nestedLevel, last])

    return (
        <>
            <Paper sx={paperMixin}>
                <Grid container spacing={{xs: 2, lg: 1}} sx={{width: '100%'}}>
                    <Grid
                        xs={showOnlyField ? true : 12}
                        sm={showOnlyField ? true : showOnlyFieldAndOperator ? 5 : 6}
                        lg={showOnlyField ? true : 3}
                    >
                        <TextField
                            select
                            fullWidth
                            label={!!rule.field ? 'Field' : undefined}
                            variant={'outlined'}
                            size={'small'}
                            disabled={disabled}
                            SelectProps={{
                                renderValue: (value) => columns.find(column => column.field === value)?.column ?? 'Select Field',
                                displayEmpty: true,
                            }}
                            value={rule.field ?? ""}
                            onChange={changeField}
                        >
                            {
                                fieldOptions.map((fieldOption) => (
                                    <MenuItem key={fieldOption.label} value={fieldOption.value}>
                                        {fieldOption.label}
                                    </MenuItem>
                                ))
                            }
                        </TextField>
                    </Grid>
                    {
                        !showOnlyField &&
                        <Grid
                            xs={12}
                            sm={showOnlyFieldAndOperator ? 5 : 6}
                            lg={3}
                        >
                            <TextField
                                select
                                fullWidth
                                label={!!rule.operator ? 'Operator' : undefined}
                                variant={'outlined'}
                                size={'small'}
                                disabled={rule.displayEmptyValuesOnly || disabled}
                                SelectProps={{
                                    renderValue: (value) => operatorOptions.find(operatorOption => operatorOption.value === value)?.label
                                        ?? 'Select Operator',
                                    displayEmpty: true,
                                }}
                                value={rule.operator ?? ""}
                                onChange={changeOperator}
                            >
                                {
                                    operatorOptions.map((operatorOption) => (
                                        <MenuItem key={operatorOption.value} value={operatorOption.value}>
                                            {operatorOption.label}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </Grid>
                    }
                    {
                        !showOnlyFieldAndOperator &&
                        <>
                            <Grid
                                xs={12}
                                sm={12}
                                md={6}
                                lg={3}
                            >
                                {
                                    !!rule.field && (!!rule.operator || !column?.operators?.length) &&
                                    <QueryBuilderValueInput
                                        column={column!}
                                        rule={rule}
                                        ruleIndex={ruleIndex}
                                        conditionIndex={conditionIndex}
                                        dispatch={dispatch}
                                        disabled={rule.displayEmptyValuesOnly || disabled}
                                    />
                                }
                            </Grid>
                            <Grid xs>
                                {
                                    nullable &&
                                    <FormControlLabel
                                        label="Show Empty Values"
                                        slotProps={{
                                            typography: {
                                                variant: 'body2',
                                            },
                                        }}
                                        sx={{margin: 0}}
                                        control={
                                            <Checkbox
                                                disabled={disabled}
                                                sx={{paddingBlock: 0.75}}
                                                disableRipple
                                                checked={rule.displayEmptyValuesOnly}
                                                onChange={changeDisplayEmptyValuesOnly}
                                            />
                                        }
                                    />
                                }
                            </Grid>
                        </>
                    }
                    <Grid
                        xs={'auto'}
                    >
                        <Tooltip title={'Remove Rule'} disableInteractive>
                            <span>
                                <IconButton
                                    color={'error'}
                                    onClick={deleteRule}
                                    disabled={disabled}
                                    sx={{backgroundColor: 'background.paper'}}
                                >
                                <DeleteOutlined/>
                            </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default QueryBuilderRule;

